// src/admin/pages/WorksAdmin.jsx
import React, { useEffect, useRef, useState } from "react";
import { db } from "../../firebase";
import {
  addDoc, collection, deleteDoc, doc, getDocs,
  orderBy, query, serverTimestamp, updateDoc
} from "firebase/firestore";

// ⬇️ si vous avez exporté `storage` dans votre firebase.js
import { storage } from "../../firebase";
import { ref as sRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const MAX_IMAGE_MB = 4;   // limite images
const MAX_VIDEO_MB = 60;  // limite vidéos

const bytesToMB = (b) => (b / (1024 * 1024)).toFixed(1);
const isImageType = (t) => t?.startsWith("image/");
const isVideoType = (t) => t?.startsWith("video/");

export default function WorksAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    title: "",
    department: "medical",
    place: "",
    date: "",
    blurb: "",
    cover: "",     // premier média (pour compat)
  });

  // 🖼️🎞️ Galerie locale (non encore sauvegardée)
  const [gallery, setGallery] = useState([]); // [{type:'image'|'video', url, bytes?, name?}]
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "works"), orderBy("createdAt","desc")));
    setList(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    setLoading(false);
  };
  useEffect(()=>{ load(); }, []);

  const onChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // =============== Upload helper (Firebase Storage) ===============
  const uploadOne = (file, destPrefix="uploads/works") => new Promise((resolve, reject) => {
    const ext = file.name?.split(".").pop() || "";
    const ts = Date.now();
    const path = `${destPrefix}/${ts}-${Math.random().toString(36).slice(2)}.${ext}`;
    const r = sRef(storage, path);
    const task = uploadBytesResumable(r, file);

    task.on("state_changed",
      // onProgress (facultatif — vous pouvez afficher une barre si besoin)
      () => {},
      (err) => reject(err),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, bytes: file.size, name: file.name, type: file.type });
      }
    );
  });

  // =============== Sélection de fichiers (multi) + validations ===============
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // validations poids
    for (const f of files) {
      if (isImageType(f.type) && f.size > MAX_IMAGE_MB * 1024 * 1024) {
        alert(`"${f.name}" est trop lourd (${bytesToMB(f.size)} Mo). Limite image: ${MAX_IMAGE_MB} Mo.`);
        return;
      }
      if (isVideoType(f.type) && f.size > MAX_VIDEO_MB * 1024 * 1024) {
        alert(`"${f.name}" est trop lourd (${bytesToMB(f.size)} Mo). Limite vidéo: ${MAX_VIDEO_MB} Mo.`);
        return;
      }
      if (!isImageType(f.type) && !isVideoType(f.type)) {
        alert(`"${f.name}" n'est ni une image ni une vidéo.`);
        return;
      }
    }

    try {
      // upload en série (simple) — vous pouvez paralléliser si besoin
      for (const f of files) {
        const up = await uploadOne(f, "uploads/works");
        const kind = isImageType(up.type) ? "image" : "video";
        setGallery((g) => [...g, { type: kind, url: up.url, bytes: up.bytes, name: up.name }]);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur pendant l’envoi de fichier(s).");
    } finally {
      // reset input pour pouvoir re-sélectionner les mêmes fichiers
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // =============== Ajout via URL (optionnel) ===============
  const [urlType, setUrlType] = useState("image");
  const [urlValue, setUrlValue] = useState("");
  const addUrl = () => {
    if (!urlValue.trim()) return;
    setGallery((g) => [...g, { type: urlType, url: urlValue.trim(), name: "URL" }]);
    setUrlValue("");
  };

  // =============== Ré-ordonnancement / suppression ===============
  const moveItem = (i, dir) => {
    setGallery((g) => {
      const n = [...g];
      const j = i + dir;
      if (j < 0 || j >= n.length) return g;
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  };
  const removeItem = (i) => {
    setGallery((g) => g.filter((_, idx) => idx !== i));
  };

  // =============== CRUD Firestore ===============
  const resetAll = () => {
    setEditing(null);
    setForm({ title:"", department:"medical", place:"", date:"", blurb:"", cover:"" });
    setGallery([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!form.title.trim()) { setError("Titre requis."); return; }
    if (gallery.length === 0) {
      // pas obligatoire, mais on peut prévenir
      if (!window.confirm("Aucun média ajouté. Enregistrer quand même ?")) return;
    }

    const payload = {
      title: form.title.trim(),
      department: form.department,
      place: form.place.trim(),
      date: form.date.trim(),
      blurb: form.blurb.trim(),
      cover: gallery[0]?.url || form.cover || "",
      gallery, // ⬅️ tableau complet (images + vidéos)
    };

    try {
      setIsSaving(true);
      setError("");
      if (editing) {
        await updateDoc(doc(db, "works", editing), { ...payload, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "works"), { ...payload, createdAt: serverTimestamp() });
      }
      resetAll();
      await load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError("Échec de l’enregistrement.");
    } finally {
      setIsSaving(false);
    }
  };

  const edit = (it) => {
    setEditing(it.id);
    setForm({
      title: it.title || "",
      department: it.department || "medical",
      place: it.place || "",
      date: it.date || "",
      blurb: it.blurb || "",
      cover: it.cover || it.imageUrl || "",
    });
    setGallery(Array.isArray(it.gallery) ? it.gallery : (it.imageUrl ? [{ type:"image", url: it.imageUrl }] : []));
    setError("");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    await deleteDoc(doc(db,"works", id));
    load();
  };

  return (
    <div>
      <h3 className="fw-bold mb-3">Réalisations</h3>

      {/* FORM */}
      <form onSubmit={save} className="card card-body shadow-sm mb-4">
        <div className="row g-3">
          <div className="col-md-8">
            <label className="form-label">Titre</label>
            <input name="title" className="form-control" value={form.title} onChange={onChange} required />
          </div>
          <div className="col-md-4">
            <label className="form-label">Département</label>
            <select name="department" className="form-select" value={form.department} onChange={onChange}>
              <option value="medical">Médical</option>
              <option value="industrial">Industriel</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Lieu</label>
            <input name="place" className="form-control" value={form.place} onChange={onChange} placeholder="Clinique, hôpital, site industriel…" />
          </div>
          <div className="col-md-6">
            <label className="form-label">Date (YYYY-MM ou YYYY-MM-DD)</label>
            <input name="date" className="form-control" value={form.date} onChange={onChange} placeholder="2025-09-01" />
          </div>

          {/* ====== MÉDIAS : uploader multi images/vidéos ====== */}
          <div className="col-12">
            <label className="form-label d-flex align-items-center justify-content-between">
              <span>Médias (images / vidéos)</span>
              <small className="text-muted">
                Limites : images ≤ {MAX_IMAGE_MB} Mo, vidéos ≤ {MAX_VIDEO_MB} Mo
              </small>
            </label>

            <div className="d-flex flex-wrap gap-2 mb-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={onPickFiles}
                className="form-control"
                style={{ maxWidth: 360 }}
              />
              <div className="d-flex align-items-center gap-2">
                <select className="form-select form-select-sm" style={{ width: 120 }} value={urlType} onChange={(e)=>setUrlType(e.target.value)}>
                  <option value="image">Image (URL)</option>
                  <option value="video">Vidéo (URL)</option>
                </select>
                <input
                  className="form-control form-control-sm"
                  placeholder="https://…"
                  value={urlValue}
                  onChange={(e)=>setUrlValue(e.target.value)}
                />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addUrl}>
                  Ajouter URL
                </button>
              </div>
            </div>

            {/* Aperçu de la galerie */}
            {gallery.length > 0 ? (
              <div className="row g-2">
                {gallery.map((m, i) => (
                  <div key={`${m.url}-${i}`} className="col-6 col-md-4 col-lg-3">
                    <div className="border rounded-3 overflow-hidden position-relative" style={{ height: 160, background: "#f8f9fa" }}>
                      {m.type === "image" ? (
                        <img src={m.url} alt={m.name || `media-${i}`} className="w-100 h-100" style={{ objectFit: "cover" }} />
                      ) : (
                        <video
                          src={m.url}
                          muted
                          controls
                          className="w-100 h-100"
                          style={{ objectFit: "cover", background:"#000" }}
                        />
                      )}

                      <div className="position-absolute top-0 start-0 m-2">
                        <span className={`badge ${m.type === "image" ? "text-bg-success" : "text-bg-dark"}`}>
                          {m.type === "image" ? "IMG" : "VID"}
                        </span>
                      </div>

                      <div className="position-absolute bottom-0 start-0 end-0 p-2 d-flex justify-content-between" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.35))" }}>
                        <div className="d-flex gap-1">
                          <button type="button" className="btn btn-sm btn-light" disabled={i===0} onClick={()=>moveItem(i,-1)} title="Monter">↑</button>
                          <button type="button" className="btn btn-sm btn-light" disabled={i===gallery.length-1} onClick={()=>moveItem(i,1)} title="Descendre">↓</button>
                        </div>
                        <button type="button" className="btn btn-sm btn-danger" onClick={()=>removeItem(i)} title="Supprimer">Suppr.</button>
                      </div>
                    </div>
                    <div className="small text-muted mt-1 text-truncate" title={m.name || m.url}>
                      {(m.name || m.url).replace(/^https?:\/\/(www\.)?/,"")}
                    </div>
                    {m.bytes ? <div className="small text-muted">{bytesToMB(m.bytes)} Mo</div> : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-light border mb-0">Aucun média ajouté pour le moment.</div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Résumé</label>
            <textarea name="blurb" rows={3} className="form-control" value={form.blurb} onChange={onChange} />
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

        <div className="mt-3 d-flex gap-2">
          <button className="btn btn-success" disabled={isSaving}>
            {isSaving ? "Enregistrement…" : (editing ? "Mettre à jour" : "Publier la réalisation")}
          </button>
          {editing && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={isSaving}
              onClick={resetAll}
            >
              Annuler
            </button>
          )}
        </div>
      </form>

      {/* LIST */}
      {loading ? (
        <div className="alert alert-light border">Chargement…</div>
      ) : (
        <div className="row g-3">
          {list.map(it => {
            const g = Array.isArray(it.gallery) ? it.gallery : [];
            const first = g[0] || (it.cover ? { type:"image", url: it.cover } : null);
            return (
              <div key={it.id} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm">
                  {first ? (
                    first.type === "video" ? (
                      <video
                        src={first.url}
                        className="card-img-top"
                        style={{ height: 180, objectFit: "cover", background:"#000" }}
                        muted
                        controls
                      />
                    ) : (
                      <img
                        src={first.url}
                        className="card-img-top"
                        alt={it.title}
                        style={{ height: 180, objectFit: "cover" }}
                      />
                    )
                  ) : (
                    <div className="bg-light" style={{ height: 180 }} />
                  )}

                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      <span className={"badge " + (it.department==="medical" ? "bg-success" : "bg-secondary")}>
                        {it.department==="medical" ? "Médical" : "Industriel"}
                      </span>
                      <small className="text-muted">{it.date || "-"}</small>
                    </div>
                    <h6 className="mt-2">{it.title}</h6>
                    <p className="small text-muted">
                      {it.place} — {it.blurb?.slice(0,90)}
                    </p>
                    {g.length > 0 && (
                      <div className="small text-muted mb-2">
                        {g.filter(m => m.type==="image").length} image(s), {g.filter(m => m.type==="video").length} vidéo(s)
                      </div>
                    )}
                    <div className="mt-auto d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary" onClick={()=>edit(it)}>Éditer</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={()=>del(it.id)}>Supprimer</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {list.length===0 && <div className="col-12"><div className="alert alert-info">Aucune réalisation.</div></div>}
        </div>
      )}
    </div>
  );
}
